import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';
import config from '../../config/appConfig';
import ToolCallCard from './ToolCallCard';
import useWebSocket from '../../hooks/useWebSocket';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { WS_ZERO_DAY_TOPICS, getZeroDayUserQueueTopic } from '../../config/wsZeroDayConfig';
import TermsOfServiceModal from '../Shared/TermsOfServiceModal';

const getCurrentTimeStr = () => {
  const now = new Date();
  return now.toTimeString().split(' ')[0].substring(0, 5);
};

const flexibleJsonParse = (str) => {
  if (!str || typeof str !== 'string') return str;
  try {
    return JSON.parse(str);
  } catch (e1) {
    try {
      // eslint-disable-next-line no-control-regex
      const sanitized = str.replace(/[\u0000-\u001F]+/g, (c) => {
        if (c === '\n') return '\\n';
        if (c === '\r') return '\\r';
        if (c === '\t') return '\\t';
        return '';
      });
      return JSON.parse(sanitized);
    } catch (e2) {
      return null;
    }
  }
};

const extractJsonEvents = (buffer) => {
  const events = [];
  let remaining = buffer;

  while (remaining.length > 0) {
    const leadingWsMatch = remaining.match(/^\s+/);
    if (leadingWsMatch) {
      remaining = remaining.slice(leadingWsMatch[0].length);
      if (!remaining) break;
    }

    if (remaining[0] !== '{') {
      const nextBrace = remaining.indexOf('{');
      if (nextBrace === -1) {
        const nl = remaining.indexOf('\n');
        if (nl !== -1) {
          const raw = remaining.slice(0, nl).trim();
          remaining = remaining.slice(nl + 1);
          if (raw) events.push({ event: 'raw_text', text: raw });
          continue;
        } else {
          break;
        }
      } else {
        const raw = remaining.slice(0, nextBrace).trim();
        remaining = remaining.slice(nextBrace);
        if (raw) events.push({ event: 'raw_text', text: raw });
        continue;
      }
    }

    let depth = 0;
    let inString = false;
    let escaped = false;
    let matchedEnd = -1;

    for (let i = 0; i < remaining.length; i++) {
      const ch = remaining[i];

      if (inString) {
        if (escaped) {
          escaped = false;
        } else if (ch === '\\') {
          escaped = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }

      if (ch === '"') {
        inString = true;
        continue;
      }

      if (ch === '{') {
        depth++;
      } else if (ch === '}') {
        depth--;
        if (depth === 0) {
          matchedEnd = i + 1;
          break;
        }
      }
    }

    if (matchedEnd !== -1) {
      const candidate = remaining.slice(0, matchedEnd);
      const parsed = flexibleJsonParse(candidate);
      if (parsed && typeof parsed === 'object') {
        events.push(parsed);
        remaining = remaining.slice(matchedEnd);
      } else {
        remaining = remaining.slice(1);
      }
    } else {
      break;
    }
  }

  return { events, remaining };
};

const ZeroDayChatCard = ({
  targetHost = '',
  targetPort = '25565',
  isConnectedToServer = false,
  agentState = 'idle',
  setAgentState,
  activeTaskIndex = 0,
  setActiveTaskIndex,
  onPlanGenerated,
  onPlanUpdate,
  onResetChat,
  onStartAgent,
  showToast,
  openModal
}) => {
  const { language } = useLanguage();
  const isEn = language === 'en';
  const { isConnected, sendMessage, subscribe } = useWebSocket();
  const { isAuthenticated, triggerGoogleLogin } = useAuth();

  const fullTarget = targetHost ? (targetPort ? `${targetHost}:${targetPort}` : targetHost) : '';

  const [aiModels, setAiModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);
  const [bottomBarState, setBottomBarState] = useState('start'); // 'start' | 'running' | 'input'
  const [inputValue, setInputValue] = useState('');
  const [isMultiline, setIsMultiline] = useState(false);
  const [isThinking, setIsThinking] = useState(false);

  // Fetch AI models dynamically from Backend (VDS API / Python AI service)
  useEffect(() => {
    let isMounted = true;
    const fetchModels = async () => {
      const endpoints = [
        config.api?.public?.aiModels,
        '/api/public/ai/models',
        '/models'
      ].filter(Boolean);

      for (const endpoint of endpoints) {
        try {
          const res = await axios.get(endpoint, { timeout: 2500 });
          const list = res.data?.data || res.data;
          if (Array.isArray(list) && list.length > 0 && isMounted) {
            setAiModels(list);
            setSelectedModel(prev => {
              const match = list.find(m => m.name === prev);
              return match ? prev : list[0].name;
            });
            return;
          }
        } catch (e) {
          // Try next fallback endpoint
        }
      }
    };
    fetchModels();
    return () => { isMounted = false; };
  }, []);

  const getInitialMessage = useCallback((target) => ({
    id: 'init-1',
    role: 'agent',
    type: 'text',
    content: target
      ? (isEn
          ? `AI agent initialized and ready for target server **${target}**.\n\nTo start autonomous zero-day vulnerability analysis, click **"Launch"** or enter instructions in the input field.`
          : `ИИ-агент готов к работе с целевым сервером **${target}**.\n\nДля старта автономного поиска и применения zero-day уязвимости нажмите кнопку **«Запустить»** или введите указание в поле ввода.`)
      : (isEn
          ? `AI agent initialized.\n\nPlease select or connect to a target Minecraft server in the card above, then click **"Launch"** to start analysis.`
          : `ИИ-агент готов к работе.\n\nПожалуйста, выберите или подключитесь к целевому серверу в карточке выше, затем нажмите **«Запустить»** для старта анализа.`),
    time: getCurrentTimeStr()
  }), [isEn]);

  // Messages in chat: { id, role: 'agent'|'user', type: 'text'|'tool', content, toolName, status, logs, time }
  const [messages, setMessages] = useState(() => [getInitialMessage(fullTarget)]);

  const chatMessagesBoxRef = useRef(null);
  const chatCardRef = useRef(null);
  const chatHeaderRef = useRef(null);
  const bottomBarRef = useRef(null);
  const placeholderRef = useRef(null);
  const modelWrapRef = useRef(null);
  const inputRef = useRef(null);
  const isStreamingRef = useRef(false);
  const eventBufferRef = useRef('');
  const activeTextIdRef = useRef(null);

  const resetStreamTracking = useCallback(() => {
    eventBufferRef.current = '';
    activeTextIdRef.current = null;
    setIsThinking(false);
  }, []);

  const finalizeRunningTools = useCallback(() => {
    setMessages(prev => prev.map(m => (m.type === 'tool' && m.status === 'running') ? { ...m, status: 'success' } : m));
    activeTextIdRef.current = null;
    setIsThinking(false);
  }, []);

  const scrollToBottom = useCallback(() => {
    if (chatMessagesBoxRef.current) {
      chatMessagesBoxRef.current.scrollTop = chatMessagesBoxRef.current.scrollHeight;
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isThinking, scrollToBottom]);

  // Update target in initial message ONLY if chat has not started yet
  useEffect(() => {
    if (agentState === 'idle') {
      setMessages(prev => {
        if (prev && prev.length <= 1) {
          return [getInitialMessage(fullTarget)];
        }
        return prev;
      });
    }
  }, [fullTarget, getInitialMessage, agentState]);


  // Click outside to close model dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modelWrapRef.current && !modelWrapRef.current.contains(e.target)) {
        setIsModelDropdownOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Floating bottom bar scroll detection
  const updateFloatingBottomBar = useCallback(() => {
    const chatCard = chatCardRef.current;
    const bottomBar = bottomBarRef.current;
    const placeholder = placeholderRef.current;
    if (!chatCard || !bottomBar || !placeholder) return;

    const cardRect = chatCard.getBoundingClientRect();
    const viewportHeight = window.innerHeight;

    // Card is visible on screen (top has entered view, and bottom hasn't completely scrolled off)
    const isCardOnScreen = cardRect.top < (viewportHeight - 40) && cardRect.bottom > 60;
    // The natural bottom of the card is cut off below the viewport
    const isBottomOffscreen = cardRect.bottom > (viewportHeight - 14);

    if (isCardOnScreen && isBottomOffscreen) {
      if (!bottomBar.classList.contains('is-floating')) {
        placeholder.style.display = 'block';
        placeholder.style.height = `${bottomBar.offsetHeight || 68}px`;
        bottomBar.classList.add('is-floating');
      }
      const inset = 16;
      bottomBar.style.left = `${cardRect.left + inset}px`;
      bottomBar.style.width = `${cardRect.width - (inset * 2)}px`;
    } else {
      if (bottomBar.classList.contains('is-floating')) {
        bottomBar.classList.remove('is-floating');
        placeholder.style.display = 'none';
        bottomBar.style.left = '';
        bottomBar.style.width = '';
      }
    }
  }, []);

  useEffect(() => {
    updateFloatingBottomBar();
    window.addEventListener('scroll', updateFloatingBottomBar, { passive: true });
    window.addEventListener('resize', updateFloatingBottomBar, { passive: true });
    return () => {
      window.removeEventListener('scroll', updateFloatingBottomBar);
      window.removeEventListener('resize', updateFloatingBottomBar);
    };
  }, [updateFloatingBottomBar]);

  useEffect(() => {
    const timer = setTimeout(updateFloatingBottomBar, 60);
    return () => clearTimeout(timer);
  }, [messages, agentState, updateFloatingBottomBar]);

  // Synchronize bottom bar state with agentState
  useEffect(() => {
    if (agentState === 'idle') {
      setBottomBarState('start');
    } else if (agentState === 'running') {
      setBottomBarState('running');
    } else if (agentState === 'paused' || agentState === 'completed') {
      setBottomBarState('input');
      setTimeout(() => inputRef.current?.focus(), 80);
    }
  }, [agentState]);

  // --- Real WebSocket Streaming Integration ---
  const handleErrorMessage = useCallback((payload) => {
    // If packet is marked as streaming chunk, it's AI text, NOT a server error!
    if (payload?.streaming) return false;

    const errorMsg = typeof payload === 'string'
      ? payload
      : (payload?.data || payload?.message || payload?.error || '');
    const code = payload?.code || '';

    if (errorMsg === 'ERROR_INSUFFICIENT_PLAN' || code === 'INSUFFICIENT_PLAN') {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      openModal?.('planError');
      showToast(isEn ? 'Professional plan required for Zero-Day audit' : 'Для запуска Zero-Day аудита требуется тариф Professional');
      return true;
    }
    if (errorMsg === 'ERROR_QUEUE_FULL' || code === 'QUEUE_FULL') {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      openModal?.('queueFull');
      return true;
    }
    if (errorMsg === 'ERROR_AI_BANNED') {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      showToast(isEn ? 'AI access temporarily restricted.' : 'Доступ к AI временно ограничен.');
      return true;
    }
    if (typeof errorMsg === 'string' && errorMsg.startsWith('ERROR_AI_SERVICE_UNAVAILABLE')) {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      showToast(isEn ? 'AI service is currently unavailable' : 'ИИ-сервис в данный момент недоступен');
      return true;
    }
    if (typeof errorMsg === 'string' && errorMsg.startsWith('ERROR_RATE_LIMIT_EXCEEDED')) {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      showToast(isEn ? 'Rate limit exceeded, please wait' : 'Превышен лимит запросов, пожалуйста подождите');
      return true;
    }
    // Explicit server error codes from WebSocket error topic
    if (code && typeof code === 'string' && code.length < 50) {
      isStreamingRef.current = false;
      setIsThinking(false);
      setAgentState('completed');
      setBottomBarState('input');
      showToast(typeof errorMsg === 'string' && errorMsg.length < 80 ? errorMsg : (isEn ? 'AI service error' : 'Ошибка ИИ-сервиса'));
      return true;
    }
    return false;
  }, [setAgentState, openModal, showToast, isEn]);

  const processStructuredEvent = useCallback((ev) => {
    if (!ev || typeof ev !== 'object') return;

    const eventType = ev.event;

    switch (eventType) {
      case 'thinking': {
        setIsThinking(Boolean(ev.isThinking));
        break;
      }

      case 'tool_start': {
        activeTextIdRef.current = null;
        setIsThinking(false);
        const newToolId = ev.toolId || `tool-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
        const toolName = ev.toolName || 'tool';

        setMessages(prev => {
          const updated = prev.map(m => (m.type === 'tool' && m.status === 'running') ? { ...m, status: 'success' } : m);
          return [
            ...updated,
            {
              id: newToolId,
              role: 'agent',
              type: 'tool',
              toolName: toolName,
              status: 'running',
              logs: [{ type: 'info', text: `[*] Выполнение ${toolName}...` }],
              time: getCurrentTimeStr()
            }
          ];
        });
        break;
      }

      case 'tool_log': {
        const tid = ev.toolId;
        const logText = typeof ev.text === 'object' ? JSON.stringify(ev.text) : (ev.text || '');
        if (!logText) break;

        const lines = logText.split('\n');
        setMessages(prev => {
          let targetIdx = -1;
          if (tid) {
            targetIdx = prev.findIndex(m => m.type === 'tool' && m.id === tid);
          }
          if (targetIdx === -1) {
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].type === 'tool' && prev[i].status === 'running') {
                targetIdx = i;
                break;
              }
            }
          }
          if (targetIdx === -1) {
            for (let i = prev.length - 1; i >= 0; i--) {
              if (prev[i].type === 'tool') {
                targetIdx = i;
                break;
              }
            }
          }

          if (targetIdx === -1) {
            const newToolId = tid || `tool-${Date.now()}`;
            return [
              ...prev,
              {
                id: newToolId,
                role: 'agent',
                type: 'tool',
                toolName: 'execute_tool',
                status: 'running',
                logs: lines.filter(Boolean).map(line => ({ text: line })),
                time: getCurrentTimeStr()
              }
            ];
          }

          const currentTool = prev[targetIdx];
          const newLogs = [...(currentTool.logs || [])];
          lines.forEach(line => {
            if (line !== '') {
              newLogs.push({ text: line });
            }
          });

          const updated = [...prev];
          updated[targetIdx] = {
            ...currentTool,
            status: 'running',
            logs: newLogs
          };
          return updated;
        });
        break;
      }

      case 'tool_end': {
        const tid = ev.toolId;
        const status = ev.status === 'error' ? 'error' : 'success';
        setMessages(prev => {
          return prev.map(m => {
            if (m.type === 'tool' && (m.id === tid || (!tid && m.status === 'running'))) {
              const logs = [...(m.logs || [])];
              if (ev.error) {
                logs.push({ type: 'error', text: `[!] ${ev.error}` });
              }
              return {
                ...m,
                status,
                logs
              };
            }
            return m;
          });
        });
        setIsThinking(true);
        break;
      }

      case 'text':
      case 'raw_text': {
        let raw = ev.text || '';
        raw = raw.replace(/\[\[TARGET:(?:ZERODAY|HACKER)\]\]/g, '');
        raw = raw.replace(/<think>[\s\S]*?<\/think>/gi, '').replace(/<\/?(?:call|think|plan)[^>]*>/gi, '');
        if (!raw) break;

        setIsThinking(false);

        setMessages(prev => {
          if (activeTextIdRef.current) {
            const idx = prev.findIndex(m => m.id === activeTextIdRef.current);
            if (idx !== -1) {
              const updated = [...prev];
              updated[idx] = {
                ...updated[idx],
                content: (updated[idx].content || '') + raw
              };
              return updated;
            }
          }

          if (prev.length > 0) {
            const last = prev[prev.length - 1];
            if (last.role === 'agent' && (last.type === 'text' || !last.type) && !last.id.startsWith('init-')) {
              activeTextIdRef.current = last.id;
              const updated = [...prev];
              updated[prev.length - 1] = {
                ...last,
                content: (last.content || '') + raw
              };
              return updated;
            }
          }

          const newTextId = `agent-text-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;
          activeTextIdRef.current = newTextId;
          return [
            ...prev,
            {
              id: newTextId,
              role: 'agent',
              type: 'text',
              content: raw,
              time: getCurrentTimeStr()
            }
          ];
        });
        break;
      }

      case 'set_plan': {
        let plan = ev.plan;
        if (typeof plan === 'string') {
          plan = flexibleJsonParse(plan) || [];
        }
        if (Array.isArray(plan)) {
          onPlanGenerated?.(plan);
        }
        break;
      }

      case 'plan_update': {
        let tasks = ev.tasks || ev.updated_tasks;
        if (typeof tasks === 'string') {
          tasks = flexibleJsonParse(tasks) || [];
        }
        if (Array.isArray(tasks)) {
          onPlanUpdate?.(tasks);
        }
        break;
      }

      case 'system': {
        const sysText = ev.text || '';
        if (!sysText) break;
        activeTextIdRef.current = null;
        setMessages(prev => [
          ...prev,
          {
            id: `sys-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`,
            role: 'agent',
            type: 'text',
            content: `> ⚠️ **[Система]** ${sysText}`,
            time: getCurrentTimeStr()
          }
        ]);
        break;
      }

      default:
        break;
    }
  }, [onPlanGenerated, onPlanUpdate]);

  const handleAiResponseChunk = useCallback((payload) => {
    if (!payload || payload.type !== 'AI_RESPONSE') return;

    // 1. Streaming content: process immediately, NEVER treat model text as an error
    if (payload.streaming) {
      isStreamingRef.current = true;
      const rawChunk = payload.data || '';
      if (!rawChunk) return;

      eventBufferRef.current += rawChunk;
      const { events, remaining } = extractJsonEvents(eventBufferRef.current);
      eventBufferRef.current = remaining;

      for (const ev of events) {
        processStructuredEvent(ev);
      }
      return;
    }

    // 2. Check for explicit error packets on non-streaming messages
    if (handleErrorMessage(payload)) {
      setIsThinking(false);
      return;
    }

    // 3. Stream finished
    if (payload.streaming === false) {
      isStreamingRef.current = false;
      if (eventBufferRef.current.trim()) {
        const { events } = extractJsonEvents(eventBufferRef.current + '\n');
        for (const ev of events) {
          processStructuredEvent(ev);
        }
        eventBufferRef.current = '';
      }
      finalizeRunningTools();
      setBottomBarState('input');
      setAgentState(prev => (prev === 'running' ? 'paused' : prev));
      return;
    }
  }, [handleErrorMessage, processStructuredEvent, finalizeRunningTools, setAgentState]);

  useEffect(() => {
    if (!isConnected) return;
    const unsubResponse = subscribe(WS_ZERO_DAY_TOPICS.AI_RESPONSE, handleAiResponseChunk);
    const unsubError = subscribe(getZeroDayUserQueueTopic(WS_ZERO_DAY_TOPICS.ERROR), handleErrorMessage);
    return () => {
      unsubResponse?.();
      unsubError?.();
    };
  }, [isConnected, subscribe, handleAiResponseChunk, handleErrorMessage]);

  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [pendingUserPrompt, setPendingUserPrompt] = useState(null);

  // Real Start Agent Execution
  const executeStart = (overridePrompt = null) => {
    if (!targetHost && !fullTarget) {
      showToast?.(isEn ? 'Please specify a target server first' : 'Сначала выберите или введите целевой сервер');
      return;
    }

    setAgentState('running');
    setBottomBarState('running');
    setIsThinking(true);
    onStartAgent?.();
    showToast(isEn ? 'Zero-Day audit started' : 'Zero-Day аудит запущен');

    const defaultPrompt = isEn
      ? `Start autonomous Zero-Day audit of target ${fullTarget}. Generate attack plan and begin reconnaissance.`
      : `Инициализируй автономный zero-day аудит цели ${fullTarget}. Сформируй план и начни разведку.`;

    const promptText = (typeof overridePrompt === 'string' && overridePrompt.trim())
      ? overridePrompt.trim()
      : defaultPrompt;

    resetStreamTracking();
    sendMessage(WS_ZERO_DAY_TOPICS.AI_PROMPT, {
      prompt: promptText,
      currentModule: 'Zero-day метод',
      history: [],
      serverAddress: fullTarget,
      aiConfig: { aiModel: selectedModel },
      resetSession: true
    });
  };

  // Start Agent Prompt (Shows Terms Modal First)
  const handleStart = (customPrompt = null) => {
    if (!targetHost && !fullTarget) {
      showToast?.(isEn ? 'Please specify a target server first' : 'Сначала выберите или введите целевой сервер');
      return;
    }

    if (!isAuthenticated) {
      triggerGoogleLogin?.();
      return;
    }

    if (!isConnected) {
      showToast(isEn ? 'Connecting to server...' : 'Подключение к серверу...');
      return;
    }

    const promptText = typeof customPrompt === 'string' && customPrompt.trim() ? customPrompt.trim() : null;
    setPendingUserPrompt(promptText);
    setIsTermsModalOpen(true);
  };

  // Stop / Pause Agent
  const handleStopOrAsk = () => {
    isStreamingRef.current = false;
    setAgentState('paused');
    setBottomBarState('input');
    setIsThinking(false);

    if (isConnected) {
      sendMessage(WS_ZERO_DAY_TOPICS.AI_STOP, {});
    }

    // Finalize currently running tool if any
    finalizeRunningTools();
    resetStreamTracking();
    setTimeout(() => inputRef.current?.focus(), 80);
  };

  // Send User Message
  const handleSend = () => {
    const text = inputValue.trim();

    if (!text) {
      // If empty in paused/completed state, resume execution
      if (agentState === 'paused' || agentState === 'completed') {
        if (!targetHost && !fullTarget) {
          showToast?.(isEn ? 'Please specify a target server first' : 'Сначала выберите или введите целевой сервер');
          return;
        }
        const continueText = isEn ? 'Continue' : 'Продолжить';
        setMessages(prev => [
          ...prev,
          {
            id: `user-${Date.now()}`,
            role: 'user',
            type: 'text',
            content: continueText,
            time: getCurrentTimeStr()
          }
        ]);
        setAgentState('running');
        setBottomBarState('running');
        setIsThinking(true);
        showToast(isEn ? 'Resuming tasks' : 'Продолжение выполнения задач');
        setInputValue('');
        setIsMultiline(false);
        if (inputRef.current) inputRef.current.style.height = '24px';
        if (isConnected) {
          resetStreamTracking();
          sendMessage(WS_ZERO_DAY_TOPICS.AI_PROMPT, {
            prompt: continueText,
            currentModule: 'Zero-day метод',
            history: [],
            serverAddress: fullTarget,
            aiConfig: { aiModel: selectedModel },
            resetSession: false
          });
        }
      }
      return;
    }

    if (!targetHost && !fullTarget) {
      showToast?.(isEn ? 'Please specify a target server first' : 'Сначала выберите или введите целевой сервер');
      return;
    }

    setMessages(prev => [
      ...prev,
      {
        id: `user-${Date.now()}`,
        role: 'user',
        type: 'text',
        content: text,
        time: getCurrentTimeStr()
      }
    ]);
    setInputValue('');
    setIsMultiline(false);
    if (inputRef.current) inputRef.current.style.height = '24px';

    if (agentState === 'idle') {
      handleStart(text);
      return;
    }

    setAgentState('running');
    setBottomBarState('running');
    setIsThinking(true);

    if (isConnected) {
      resetStreamTracking();
      sendMessage(WS_ZERO_DAY_TOPICS.AI_PROMPT, {
        prompt: text,
        currentModule: 'Zero-day метод',
        history: [],
        serverAddress: fullTarget,
        aiConfig: { aiModel: selectedModel },
        resetSession: false
      });
    } else {
      showToast(isEn ? 'Offline: message not sent' : 'Офлайн: сообщение не отправлено');
    }
  };

  // Reset Chat
  const handleResetChat = () => {
    if (isConnected) {
      sendMessage(WS_ZERO_DAY_TOPICS.AI_RESET, {});
    }
    isStreamingRef.current = false;
    setAgentState('idle');
    setActiveTaskIndex(0);
    onResetChat?.();
    setBottomBarState('start');
    setInputValue('');
    setIsMultiline(false);
    setIsThinking(false);
    if (inputRef.current) inputRef.current.style.height = '24px';
    finalizeRunningTools();
    resetStreamTracking();

    setMessages([
      {
        ...getInitialMessage(fullTarget),
        id: `init-${Date.now()}`
      }
    ]);

    showToast(isEn ? 'Chat and tasks reset' : 'Чат и задачи сброшены');
  };

  // Model Selection
  const handleSelectModel = (model) => {
    setSelectedModel(model.name);
    setIsModelDropdownOpen(false);
    showToast(isEn ? `Model switched: ${model.name}` : `Модель переключена: ${model.name}`);
  };

  return (
    <section className="ai-chat-card" ref={chatCardRef}>
      {/* Chat Header */}
      <div className="chat-header" ref={chatHeaderRef}>
        <div className="chat-agent-info">
          <span className="chat-agent-name">Storm Zero-Day Agent</span>
        </div>

        <div className="chat-header-actions">
          <button
            className="chat-head-btn"
            title={isEn ? 'Clear dialog' : 'Очистить диалог'}
            onClick={handleResetChat}
          >
            <svg viewBox="0 0 24 24">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
        </div>
      </div>

      {/* Messages Scroll Box (NO AVATARS) */}
      <div className="chat-messages-scroll" ref={chatMessagesBoxRef}>
        {messages.map((msg) => {
          if (msg.type === 'tool') {
            return (
              <ToolCallCard
                key={msg.id}
                toolName={msg.toolName}
                status={msg.status}
                logs={msg.logs}
              />
            );
          }

          return (
            <div key={msg.id} className={`chat-msg ${msg.role}`}>
              <div className="msg-bubble">
                <ReactMarkdown>{msg.content}</ReactMarkdown>
                <span className="msg-time">{msg.time}</span>
              </div>
            </div>
          );
        })}

        {isThinking && (
          <div className="chat-msg agent thinking-msg">
            <div className="msg-bubble thinking-bubble">
              <div className="thinking-row">
                <span className="thinking-text">Thinking</span>
              </div>
              <span className="msg-time">{getCurrentTimeStr()}</span>
            </div>
          </div>
        )}
      </div>

      {/* Floating Placeholder */}
      <div className="chat-bottom-bar-placeholder" ref={placeholderRef} />

      {/* Dynamic Bottom Bar */}
      <div className="chat-bottom-bar" ref={bottomBarRef}>
        <div className="chat-action-container">
          {bottomBarState === 'start' && (
            <button className="chat-launch-btn" onClick={() => handleStart()}>
              <svg viewBox="0 0 24 24">
                <polygon points="5 3 19 12 5 21 5 3" />
              </svg>
              <span>{isEn ? 'Launch' : 'Запустить'}</span>
            </button>
          )}

          {bottomBarState === 'running' && (
            <button className="chat-stop-btn" onClick={handleStopOrAsk}>
              <svg viewBox="0 0 24 24">
                <rect x="6" y="4" width="4" height="16" />
                <rect x="14" y="4" width="4" height="16" />
              </svg>
              <span>{isEn ? 'Stop or ask' : 'Остановить или спросить'}</span>
            </button>
          )}

          {bottomBarState === 'input' && (
            <div className={`chat-input-box ${isMultiline ? 'multiline' : ''}`}>
              <svg className="search-input-icon" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <textarea
                ref={inputRef}
                rows={1}
                className="chat-text-input outline-none border-0 ring-0 focus:outline-none focus:ring-0 focus:border-0 shadow-none focus:shadow-none resize-none min-h-0"
                value={inputValue}
                onChange={(e) => {
                  const val = e.target.value;
                  setInputValue(val);
                  e.target.style.height = 'auto';
                  const scrollH = e.target.scrollHeight;
                  const multiline = val.includes('\n') || scrollH > 32;
                  setIsMultiline(multiline);
                  if (multiline) {
                    e.target.style.height = `${Math.min(scrollH, 120)}px`;
                  } else {
                    e.target.style.height = '26px';
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder={
                  agentState === 'paused'
                    ? (isEn ? 'Write "Continue" to proceed with audit...' : 'Напишите «Продолжить» для продолжения аудита...')
                    : (agentState === 'completed'
                      ? (isEn ? 'Ask questions regarding audit results...' : 'Задайте вопрос по результатам аудита...')
                      : (isEn ? 'Ask a question to the agent (Shift+Enter for newline)...' : 'Задайте вопрос агенту (Shift+Enter для переноса)...'))
                }
              />
              <button
                className="chat-send-btn"
                onClick={handleSend}
                title={isEn ? 'Send message or resume' : 'Отправить сообщение или продолжить'}
              >
                <svg viewBox="0 0 24 24">
                  <line x1="22" y1="2" x2="11" y2="13" />
                  <polygon points="22 2 15 22 11 13 2 9 22 2" />
                </svg>
              </button>
            </div>
          )}
        </div>

        {/* Model Selector Dropdown (Hidden only during running state by design) */}
        {bottomBarState !== 'running' && (
          <div
            className={`model-selector-wrap ${isModelDropdownOpen ? 'open' : ''}`}
            ref={modelWrapRef}
          >
            <button
              type="button"
              className="model-select-btn"
              onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
            >
              <svg className="model-icon" viewBox="0 0 24 24">
                <path d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm1 14.93V17a1 1 0 0 1-2 0v-.07A8 8 0 0 1 4.07 11H4a1 1 0 0 1 0-2h.07A8 8 0 0 1 11 4.07V4a1 1 0 0 1 2 0v.07A8 8 0 0 1 19.93 11H20a1 1 0 0 1 0 2h-.07A8 8 0 0 1 13 16.93z" />
              </svg>
              <span>{selectedModel || (isEn ? 'AI Model' : 'Модель ИИ')}</span>
              <svg className="chevron-icon" viewBox="0 0 24 24">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>

            {/* Popover Dropdown Menu */}
            <div className="model-dropdown-menu">
              <div className="model-dropdown-header">
                {isEn ? 'AI Reasoning Model' : 'Модель рассуждения ИИ'}
              </div>

              {aiModels.length === 0 ? (
                <div className="model-dropdown-empty">
                  {isEn ? 'Loading models from server...' : 'Загрузка моделей с сервера...'}
                </div>
              ) : (
                aiModels.map((m) => {
                  const isActive = selectedModel === m.name;
                  return (
                    <button
                      key={m.name}
                      type="button"
                      className={`model-item ${isActive ? 'active' : ''}`}
                      onClick={() => handleSelectModel(m)}
                    >
                      <div className="model-item-info">
                        <span className="model-item-title">{m.name}</span>
                        <span className="model-item-sub">{isEn ? (m.descEn || m.desc) : (m.desc || m.descEn)}</span>
                      </div>
                      <svg
                        className="model-item-check"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}
      </div>

      {/* Legal Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => {
          setIsTermsModalOpen(false);
          setPendingUserPrompt(null);
        }}
        onAccept={() => {
          setIsTermsModalOpen(false);
          const prompt = typeof pendingUserPrompt === 'string' ? pendingUserPrompt : null;
          executeStart(prompt);
          setPendingUserPrompt(null);
        }}
        onDecline={() => {
          setIsTermsModalOpen(false);
          setPendingUserPrompt(null);
        }}
      />
    </section>
  );
};

export default ZeroDayChatCard;
